<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import getWootAxios from 'dashboard/helper/getWootAxios';
import {
  downloadCsvFile,
  generateFileName,
} from 'dashboard/helper/downloadHelper';
import format from 'date-fns/format';
import fromUnixTime from 'date-fns/fromUnixTime';
import BarChart from 'shared/components/charts/BarChart.vue';
import {
  buildDatasetHoverDimmingHandler,
  buildIntercomLikeBarTooltip,
  removeTooltipEl,
} from 'shared/components/charts/barInteractions';
import CompactDateInboxFilters from './components/CompactDateInboxFilters.vue';

const route = useRoute();
const router = useRouter();
const showAlert = useAlert;

const accountId = route.params.accountId;

const from = ref(0);
const to = ref(0);
const isLoading = ref(false);
const errorMessage = ref('');
const generatedAt = ref(null);

const filters = computed(() => ({
  assistant_id: route.query.assistant_id
    ? Number(route.query.assistant_id)
    : null,
  inbox_id: route.query.inbox_id ? Number(route.query.inbox_id) : null,
  conversation_id: route.query.conversation_id
    ? Number(route.query.conversation_id)
    : null,
}));

const capabilities = ref({
  can_view_costing: false,
  can_view_model_breakdown: false,
});

const summary = ref({
  prompt_tokens: 0,
  completion_tokens: 0,
  total_tokens: 0,
  events_count: 0,
  estimated_cost_usd: null,
  cost_per_chat_usd: null,
});
const timeseries = ref([]);
const byModel = ref([]);
const byEndpoint = ref([]);
const byPurpose = ref([]);
const byAssistant = ref([]);
const byInbox = ref([]);
const byConversation = ref([]);
const costPerChatByModel = ref([]);

const showCosting = computed(() =>
  Boolean(capabilities.value?.can_view_costing)
);
const showModelBreakdown = computed(() =>
  Boolean(capabilities.value?.can_view_model_breakdown)
);

const fetchData = async () => {
  isLoading.value = true;
  errorMessage.value = '';
  try {
    const response = await getWootAxios().get(
      `/api/v2/accounts/${accountId}/llm_usage_reports`,
      {
        params: {
          since: from.value,
          until: to.value,
          assistant_id: filters.value.assistant_id,
          inbox_id: filters.value.inbox_id,
          conversation_id: filters.value.conversation_id,
        },
      }
    );
    capabilities.value = response.data?.capabilities || capabilities.value;
    summary.value = response.data?.summary || summary.value;
    timeseries.value = response.data?.timeseries || [];
    byModel.value = response.data?.by_model || [];
    byEndpoint.value = response.data?.by_endpoint || [];
    byPurpose.value = response.data?.by_purpose || [];
    byAssistant.value = response.data?.by_assistant || [];
    byInbox.value = response.data?.by_inbox || [];
    byConversation.value = response.data?.by_conversation || [];
    costPerChatByModel.value = response.data?.cost_per_chat_by_model || [];
    generatedAt.value = new Date();
  } catch (e) {
    showAlert('❌ Failed to load token usage report');
    errorMessage.value =
      'Failed to fetch token usage report. Please try again.';
  } finally {
    isLoading.value = false;
  }
};

function setDrilldown(next) {
  const query = { ...route.query, ...next };
  Object.keys(query).forEach(k => {
    if (query[k] === null || query[k] === undefined || query[k] === '')
      delete query[k];
  });
  router.push({ query });
}

const onFilterChange = ({ from: f, to: t, selectedInbox }) => {
  from.value = f;
  to.value = t;

  const nextInboxId = selectedInbox?.id || null;
  if (nextInboxId !== filters.value.inbox_id) {
    setDrilldown({ inbox_id: nextInboxId });
    return;
  }

  fetchData();
};

const onAssistantRowClick = row => {
  if (!row?.assistant_id) return;
  setDrilldown({ assistant_id: row.assistant_id });
};

const onInboxRowClick = row => {
  if (!row?.inbox_id) return;
  setDrilldown({ inbox_id: row.inbox_id });
};

const onConversationRowClick = row => {
  if (!row?.conversation_id) return;
  setDrilldown({ conversation_id: row.conversation_id });
};

watch(
  () => route.query,
  () => {
    // Only refetch if a date range has been selected.
    if (from.value > 0 && to.value > 0) fetchData();
  }
);

const formatUsd = v => {
  if (v === null || v === undefined) return '-';
  const n = Number(v);
  if (Number.isNaN(n)) return '-';
  return `$${n.toFixed(6)}`;
};

const periodLabel = computed(() => {
  if (!from.value || !to.value) return '—';
  return `${format(fromUnixTime(from.value), 'dd MMM yy')} - ${format(
    fromUnixTime(to.value),
    'dd MMM yy'
  )}`;
});

const generatedAtLabel = computed(() => {
  if (!generatedAt.value) return '—';
  return generatedAt.value.toLocaleString();
});

const toCsvValue = value => {
  const stringValue = String(value ?? '');
  if (/[",\n]/.test(stringValue)) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
};

const rowsToCsv = (headers, rows) => {
  const csvRows = [headers, ...rows];
  return csvRows
    .map(row => row.map(column => toCsvValue(column)).join(','))
    .join('\n');
};

const downloadDailyUsageCsv = () => {
  const headers = [
    'Day',
    'Total tokens',
    'Prompt tokens',
    'Completion tokens',
    'Events',
    ...(showCosting.value ? ['Estimated cost (USD)'] : []),
  ];

  const rows = (timeseries.value || []).map(row => [
    format(fromUnixTime(row.day), 'dd MMM yy'),
    Number(row.total_tokens || 0),
    Number(row.prompt_tokens || 0),
    Number(row.completion_tokens || 0),
    Number(row.events_count || 0),
    ...(showCosting.value ? [row.estimated_cost_usd ?? ''] : []),
  ]);

  const csvContent = rowsToCsv(headers, rows);
  const fileName = generateFileName({
    type: 'token-usage-daily',
    to: to.value,
  });
  downloadCsvFile(fileName, csvContent);
};

const sortedTimeseries = computed(() => {
  const rows = Array.isArray(timeseries.value) ? [...timeseries.value] : [];
  rows.sort((a, b) => Number(a.day || 0) - Number(b.day || 0));
  return rows;
});

const hasChartData = computed(() => sortedTimeseries.value.length > 0);

const chartCollection = computed(() => ({
  labels: sortedTimeseries.value.map(row =>
    format(fromUnixTime(row.day), 'dd MMM')
  ),
  datasets: [
    {
      label: 'Prompt tokens',
      data: sortedTimeseries.value.map(row => Number(row.prompt_tokens || 0)),
      backgroundColor: '#1D75E5',
      borderRadius: 4,
      categoryPercentage: 0.72,
      barPercentage: 0.95,
      stack: 'tokens',
    },
    {
      label: 'Completion tokens',
      data: sortedTimeseries.value.map(row =>
        Number(row.completion_tokens || 0)
      ),
      backgroundColor: '#22c55e',
      borderRadius: 4,
      categoryPercentage: 0.72,
      barPercentage: 0.95,
      stack: 'tokens',
    },
  ],
}));

const tooltipId = 'token-usage-tooltip';

const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  animation: { duration: 0 },
  interaction: { mode: 'nearest', intersect: true },
  onHover: buildDatasetHoverDimmingHandler(),
  plugins: {
    legend: { display: false },
    tooltip: {
      enabled: false,
      displayColors: false,
      external: buildIntercomLikeBarTooltip({
        tooltipId,
        titleForIndex: index =>
          format(
            fromUnixTime(sortedTimeseries.value[index]?.day || 0),
            "dd MMM''yy"
          ),
        seriesLabelForDataset: datasetIndex =>
          datasetIndex === 0 ? 'Prompt tokens' : 'Completion tokens',
        showPercent: false,
      }),
    },
  },
  scales: {
    x: {
      stacked: true,
      grid: { drawOnChartArea: false },
    },
    y: {
      stacked: true,
      beginAtZero: true,
      ticks: { callback: value => Number(value).toLocaleString() },
      grid: { drawOnChartArea: false },
    },
  },
}));

const drilldownLabel = computed(() => {
  const parts = [];
  if (filters.value.assistant_id)
    parts.push(`assistant=${filters.value.assistant_id}`);
  if (filters.value.inbox_id) parts.push(`inbox=${filters.value.inbox_id}`);
  if (filters.value.conversation_id)
    parts.push(`conversation=${filters.value.conversation_id}`);
  return parts.join(', ');
});

const clearDrilldown = () => {
  setDrilldown({ assistant_id: null, inbox_id: null, conversation_id: null });
};

onBeforeUnmount(() => {
  removeTooltipEl(tooltipId);
});
</script>

<template>
  <div class="tu-page">
    <div class="tu-shell">
      <div class="tu-header">
        <div class="tu-header-left">
          <h1 class="tu-title">Token usage</h1>
        </div>
      </div>

      <div class="tu-toolbar">
        <CompactDateInboxFilters
          :show-add-filter="false"
          :forced-inbox-id="filters.inbox_id"
          @filter-change="onFilterChange"
        />
        <div v-if="drilldownLabel" class="tu-drilldown">
          <span class="tu-drilldown__label">
            Drill-down: {{ drilldownLabel }}
          </span>
          <button
            class="tu-drilldown__clear"
            type="button"
            @click="clearDrilldown"
          >
            Clear
          </button>
        </div>
      </div>

      <div class="tu-content">
        <div v-if="errorMessage" class="tu-error">
          {{ errorMessage }}
        </div>

        <section class="tu-card">
          <div class="tu-card-header">
            <div class="tu-card-title-wrap">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="20"
                height="20"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 8h.01M11 12h1v4h1M3 12a9 9 0 1 0 18.001 0A9 9 0 0 0 3 12"
                />
              </svg>
              <h2 class="tu-card-title">Daily usage</h2>
              <span class="tu-period">{{ periodLabel }}</span>
            </div>
            <button
              class="tu-export-btn"
              type="button"
              @click="downloadDailyUsageCsv"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="16"
                height="16"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2m-8-1V4m0 12 5-5m-5 5-5-5"
                />
              </svg>
              <span>Export CSV</span>
            </button>
          </div>

          <div class="tu-section tu-section--no-bottom">
            <div
              class="tu-metric-row"
              :class="{ 'tu-metric-row--cost': showCosting }"
            >
              <div class="tu-metric">
                <div class="tu-metric-label">Total tokens</div>
                <div class="tu-metric-value">
                  {{ Number(summary.total_tokens || 0).toLocaleString() }}
                </div>
              </div>
              <div class="tu-metric">
                <div class="tu-metric-label">Prompt tokens</div>
                <div class="tu-metric-value">
                  {{ Number(summary.prompt_tokens || 0).toLocaleString() }}
                </div>
              </div>
              <div class="tu-metric">
                <div class="tu-metric-label">Completion tokens</div>
                <div class="tu-metric-value">
                  {{ Number(summary.completion_tokens || 0).toLocaleString() }}
                </div>
              </div>
              <div class="tu-metric">
                <div class="tu-metric-label">Events</div>
                <div class="tu-metric-value">
                  {{ Number(summary.events_count || 0).toLocaleString() }}
                </div>
              </div>
              <div v-if="showCosting" class="tu-metric">
                <div class="tu-metric-label">Estimated cost (USD)</div>
                <div class="tu-metric-value">
                  {{ formatUsd(summary.estimated_cost_usd) }}
                </div>
              </div>
              <div v-if="showCosting" class="tu-metric">
                <div class="tu-metric-label">Cost per chat</div>
                <div class="tu-metric-value">
                  {{ formatUsd(summary.cost_per_chat_usd) }}
                </div>
              </div>
            </div>

            <div class="tu-chart">
              <woot-loading-state
                v-if="isLoading"
                :message="$t('REPORT.LOADING_CHART')"
                class="h-full"
              />
              <div v-else-if="!hasChartData" class="tu-empty">
                No data for this period.
              </div>
              <BarChart
                v-else
                :collection="chartCollection"
                :chart-options="chartOptions"
              />
            </div>
          </div>
        </section>

        <div class="tu-grid tu-grid--two">
          <section v-if="showModelBreakdown" class="tu-card">
            <div class="tu-card-header">
              <div class="tu-card-title-wrap">
                <h2 class="tu-card-title">By model</h2>
              </div>
            </div>
            <div class="tu-section">
              <div v-if="isLoading" class="tu-muted">Loading…</div>
              <table v-else class="tu-table">
                <thead>
                  <tr>
                    <th>Model</th>
                    <th class="num">Tokens</th>
                    <th class="num">Events</th>
                    <th v-if="showCosting" class="num">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in byModel" :key="row.model">
                    <td>{{ row.model }}</td>
                    <td class="num">
                      {{ Number(row.total_tokens || 0).toLocaleString() }}
                    </td>
                    <td class="num">
                      {{ Number(row.events_count || 0).toLocaleString() }}
                    </td>
                    <td v-if="showCosting" class="num">
                      {{ formatUsd(row.estimated_cost_usd) }}
                    </td>
                  </tr>
                  <tr v-if="!byModel.length">
                    <td :colspan="showCosting ? 4 : 3" class="tu-muted">
                      No data
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section v-if="showCosting" class="tu-card">
            <div class="tu-card-header">
              <div class="tu-card-title-wrap">
                <h2 class="tu-card-title">Cost per chat by model</h2>
              </div>
            </div>
            <div class="tu-section">
              <div v-if="isLoading" class="tu-muted">Loading…</div>
              <table v-else class="tu-table">
                <thead>
                  <tr>
                    <th>Model</th>
                    <th class="num">Chats</th>
                    <th class="num">Cost / chat</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in costPerChatByModel" :key="row.model">
                    <td>{{ row.model }}</td>
                    <td class="num">
                      {{ Number(row.chat_events_count || 0).toLocaleString() }}
                    </td>
                    <td class="num">{{ formatUsd(row.cost_per_chat_usd) }}</td>
                  </tr>
                  <tr v-if="!costPerChatByModel.length">
                    <td colspan="3" class="tu-muted">No data</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section class="tu-card">
            <div class="tu-card-header">
              <div class="tu-card-title-wrap">
                <h2 class="tu-card-title">By endpoint</h2>
              </div>
            </div>
            <div class="tu-section">
              <div v-if="isLoading" class="tu-muted">Loading…</div>
              <table v-else class="tu-table">
                <thead>
                  <tr>
                    <th>Endpoint</th>
                    <th class="num">Tokens</th>
                    <th class="num">Events</th>
                    <th v-if="showCosting" class="num">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in byEndpoint" :key="row.endpoint">
                    <td class="tu-truncate" :title="row.endpoint">
                      {{ row.endpoint }}
                    </td>
                    <td class="num">
                      {{ Number(row.total_tokens || 0).toLocaleString() }}
                    </td>
                    <td class="num">
                      {{ Number(row.events_count || 0).toLocaleString() }}
                    </td>
                    <td v-if="showCosting" class="num">
                      {{ formatUsd(row.estimated_cost_usd) }}
                    </td>
                  </tr>
                  <tr v-if="!byEndpoint.length">
                    <td :colspan="showCosting ? 4 : 3" class="tu-muted">
                      No data
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        </div>

        <section class="tu-card">
          <div class="tu-card-header">
            <div class="tu-card-title-wrap">
              <h2 class="tu-card-title">By purpose</h2>
              <span class="tu-subtitle">
                Helps verify what’s spending tokens (e.g., contact_profile,
                conversation_faq, brand_memory_extractor).
              </span>
            </div>
          </div>
          <div class="tu-section">
            <div v-if="isLoading" class="tu-muted">Loading…</div>
            <table v-else class="tu-table">
              <thead>
                <tr>
                  <th>Purpose</th>
                  <th class="num">Tokens</th>
                  <th class="num">Events</th>
                  <th v-if="showCosting" class="num">Cost</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in byPurpose" :key="row.purpose">
                  <td class="tu-truncate" :title="row.purpose">
                    {{ row.purpose }}
                  </td>
                  <td class="num">
                    {{ Number(row.total_tokens || 0).toLocaleString() }}
                  </td>
                  <td class="num">
                    {{ Number(row.events_count || 0).toLocaleString() }}
                  </td>
                  <td v-if="showCosting" class="num">
                    {{ formatUsd(row.estimated_cost_usd) }}
                  </td>
                </tr>
                <tr v-if="!byPurpose.length">
                  <td :colspan="showCosting ? 4 : 3" class="tu-muted">
                    No data
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <div class="tu-grid tu-grid--two">
          <section class="tu-card">
            <div class="tu-card-header">
              <div class="tu-card-title-wrap">
                <h2 class="tu-card-title">By assistant</h2>
              </div>
            </div>
            <div class="tu-section">
              <div v-if="isLoading" class="tu-muted">Loading…</div>
              <table v-else class="tu-table">
                <thead>
                  <tr>
                    <th>Assistant</th>
                    <th class="num">Tokens</th>
                    <th class="num">Events</th>
                    <th v-if="showCosting" class="num">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="row in byAssistant"
                    :key="row.assistant_id || 'none'"
                    :class="{ 'tu-clickable': Boolean(row.assistant_id) }"
                    @click="onAssistantRowClick(row)"
                  >
                    <td class="tu-truncate" :title="row.assistant_name || ''">
                      {{
                        row.assistant_name || `(id: ${row.assistant_id || '—'})`
                      }}
                    </td>
                    <td class="num">
                      {{ Number(row.total_tokens || 0).toLocaleString() }}
                    </td>
                    <td class="num">
                      {{ Number(row.events_count || 0).toLocaleString() }}
                    </td>
                    <td v-if="showCosting" class="num">
                      {{ formatUsd(row.estimated_cost_usd) }}
                    </td>
                  </tr>
                  <tr v-if="!byAssistant.length">
                    <td :colspan="showCosting ? 4 : 3" class="tu-muted">
                      No data
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section class="tu-card">
            <div class="tu-card-header">
              <div class="tu-card-title-wrap">
                <h2 class="tu-card-title">By inbox</h2>
              </div>
            </div>
            <div class="tu-section">
              <div v-if="isLoading" class="tu-muted">Loading…</div>
              <table v-else class="tu-table">
                <thead>
                  <tr>
                    <th>Inbox</th>
                    <th class="num">Tokens</th>
                    <th class="num">Events</th>
                    <th v-if="showCosting" class="num">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="row in byInbox"
                    :key="row.inbox_id || 'none'"
                    :class="{ 'tu-clickable': Boolean(row.inbox_id) }"
                    @click="onInboxRowClick(row)"
                  >
                    <td class="tu-truncate" :title="row.inbox_name || ''">
                      {{ row.inbox_name || `(id: ${row.inbox_id || '—'})` }}
                    </td>
                    <td class="num">
                      {{ Number(row.total_tokens || 0).toLocaleString() }}
                    </td>
                    <td class="num">
                      {{ Number(row.events_count || 0).toLocaleString() }}
                    </td>
                    <td v-if="showCosting" class="num">
                      {{ formatUsd(row.estimated_cost_usd) }}
                    </td>
                  </tr>
                  <tr v-if="!byInbox.length">
                    <td :colspan="showCosting ? 4 : 3" class="tu-muted">
                      No data
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        </div>

        <section class="tu-card">
          <div class="tu-card-header">
            <div class="tu-card-title-wrap">
              <h2 class="tu-card-title">Top conversations</h2>
            </div>
          </div>
          <div class="tu-section">
            <div v-if="isLoading" class="tu-muted">Loading…</div>
            <table v-else class="tu-table">
              <thead>
                <tr>
                  <th>Conversation</th>
                  <th class="num">Tokens</th>
                  <th class="num">Events</th>
                  <th v-if="showCosting" class="num">Cost</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in byConversation"
                  :key="row.conversation_id"
                  :class="{ 'tu-clickable': Boolean(row.conversation_id) }"
                  @click="onConversationRowClick(row)"
                >
                  <td>
                    #{{ row.conversation_display_id || row.conversation_id }}
                  </td>
                  <td class="num">
                    {{ Number(row.total_tokens || 0).toLocaleString() }}
                  </td>
                  <td class="num">
                    {{ Number(row.events_count || 0).toLocaleString() }}
                  </td>
                  <td v-if="showCosting" class="num">
                    {{ formatUsd(row.estimated_cost_usd) }}
                  </td>
                </tr>
                <tr v-if="!byConversation.length">
                  <td :colspan="showCosting ? 4 : 3" class="tu-muted">
                    No data
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section class="tu-card">
          <div class="tu-card-header">
            <div class="tu-card-title-wrap">
              <h2 class="tu-card-title">Daily usage (table)</h2>
            </div>
          </div>
          <div class="tu-section">
            <div v-if="isLoading" class="tu-muted">Loading…</div>
            <table v-else class="tu-table">
              <thead>
                <tr>
                  <th>Day</th>
                  <th class="num">Total</th>
                  <th class="num">Prompt</th>
                  <th class="num">Completion</th>
                  <th class="num">Events</th>
                  <th v-if="showCosting" class="num">Cost</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in sortedTimeseries" :key="row.day">
                  <td>{{ format(fromUnixTime(row.day), 'dd MMM yy') }}</td>
                  <td class="num">
                    {{ Number(row.total_tokens || 0).toLocaleString() }}
                  </td>
                  <td class="num">
                    {{ Number(row.prompt_tokens || 0).toLocaleString() }}
                  </td>
                  <td class="num">
                    {{ Number(row.completion_tokens || 0).toLocaleString() }}
                  </td>
                  <td class="num">
                    {{ Number(row.events_count || 0).toLocaleString() }}
                  </td>
                  <td v-if="showCosting" class="num">
                    {{ formatUsd(row.estimated_cost_usd) }}
                  </td>
                </tr>
                <tr v-if="!sortedTimeseries.length">
                  <td :colspan="showCosting ? 6 : 5" class="tu-muted">
                    No data
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <p class="tu-footer-note">Generated at {{ generatedAtLabel }}</p>
      </div>
    </div>
  </div>
</template>

<style scoped>
.tu-page {
  min-height: 100vh;
  min-height: 100dvh;
  width: 100%;
  background: #ffffff;
  color: #111827;
}

.tu-shell {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #ffffff;
  overflow: visible;
  min-width: 650px;
}

.tu-header {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  min-height: 58px;
  padding: 0 24px;
  border-bottom: 1px solid #eceff3;
  position: sticky;
  top: 0;
  z-index: 50;
  background: #ffffff;
}

.tu-header-left {
  display: flex;
  align-items: center;
  gap: 0;
}

.tu-title {
  margin: 0;
  font-size: 18px !important;
  line-height: 24px;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: #111827;
}

.tu-toolbar {
  padding: 12px 24px 8px;
  background: #ffffff;
  box-shadow: rgba(0, 0, 0, 0.3) 0 3px 10px -6px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  position: sticky;
  top: 58px;
  z-index: 49;
}

.tu-drilldown {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
}

.tu-drilldown__label {
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
}

.tu-drilldown__clear {
  font-size: 12px;
  font-weight: 700;
  color: #111827;
  text-decoration: underline;
  background: transparent;
  border: none;
  cursor: pointer;
}

.tu-content {
  padding: 16px 32px;
}

.tu-error {
  margin-bottom: 12px;
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #b91c1c;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 13px;
}

.tu-card {
  border-radius: 4px;
  border: 1px solid #d8dee5;
  background: #ffffff;
  box-sizing: border-box;
  margin-bottom: 20px;
  overflow: hidden;
}

.tu-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 20px;
  border-bottom: 1px solid #e5e7eb;
}

.tu-card-title-wrap {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex-wrap: wrap;
}

.tu-card-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #111827;
}

.tu-period {
  font-size: 12px;
  color: #6b7280;
  margin-left: 10px;
  white-space: nowrap;
}

.tu-subtitle {
  font-size: 12px;
  color: #6b7280;
  margin-left: 10px;
}

.tu-export-btn {
  min-width: 128px;
  height: 36px;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  background: #ffffff;
  color: #111827;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}

.tu-export-btn:hover {
  background: #f9fafb;
}

.tu-section {
  padding: 16px 20px 20px;
}

.tu-section--no-bottom {
  padding-bottom: 0;
}

.tu-metric-row {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}

.tu-metric-row--cost {
  grid-template-columns: repeat(6, minmax(0, 1fr));
}

@media (max-width: 1100px) {
  .tu-metric-row,
  .tu-metric-row--cost {
    grid-template-columns: 1fr;
  }
}

.tu-metric {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #f8fafc;
  padding: 12px;
}

.tu-metric-label {
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
}

.tu-metric-value {
  margin-top: 8px;
  font-size: 20px;
  line-height: 24px;
  font-weight: 800;
  color: #111827;
}

.tu-chart {
  height: 384px;
  margin-top: 16px;
}

.tu-empty {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  font-size: 13px;
}

.tu-grid {
  display: grid;
  gap: 16px;
}

.tu-grid--two {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

@media (max-width: 1100px) {
  .tu-grid--two {
    grid-template-columns: 1fr;
  }
}

.tu-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.tu-table th,
.tu-table td {
  border-bottom: 1px solid #edf1f5;
  padding: 10px 8px;
  text-align: left;
}

.tu-table th {
  font-weight: 800;
  color: #111827;
  background: #f8fafc;
  position: sticky;
  top: 0;
}

.tu-table th.num,
.tu-table td.num {
  text-align: right;
}

.tu-muted {
  color: #6b7280;
  font-size: 12px;
  padding: 8px 0;
}

.tu-clickable {
  cursor: pointer;
}

.tu-clickable:hover td {
  background: #f9fafb;
}

.tu-truncate {
  max-width: 420px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tu-footer-note {
  margin-top: 8px;
  font-size: 12px;
  color: #6b7280;
  line-height: 1.5;
}

::global(body.dark) .tu-page,
::global(body.dark) .tu-shell,
::global(body.dark) .tu-card,
::global(body.dark) .tu-header,
::global(body.dark) .tu-toolbar,
::global(body.theme-mix) .tu-page,
::global(body.theme-mix) .tu-shell,
::global(body.theme-mix) .tu-card,
::global(body.theme-mix) .tu-header,
::global(body.theme-mix) .tu-toolbar {
  background: #ffffff !important;
  color: #111827 !important;
}
</style>
